import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Stack, TextField, Typography,
} from '@mui/material';
import {
  createAccountBindingReviewController,
  type AccountBindingReviewController,
  type AccountBindingSelection,
} from '../../services/accountBindingReviewService';

interface Props {
  open: boolean;
  transport: (payload: unknown) => Promise<unknown>;
  onClose: () => void;
  onPrepared: (controller: AccountBindingReviewController) => void;
}

interface FormValues {
  authUid: string;
  formerUserId: string;
  targetUserId: string;
  targetPersonId: string;
  operationKey: string;
  frozenInventorySha256: string;
  expectedBindingRevision: string;
  expectedBindingOperationDigest: string;
}
const emptyValues: FormValues = {
  authUid: '', formerUserId: '', targetUserId: '', targetPersonId: '', operationKey: '',
  frozenInventorySha256: '', expectedBindingRevision: '0', expectedBindingOperationDigest: '',
};
const safeId = (value: string, limit: number) => value.length > 0 && value.length <= limit
  && value === value.trim() && !/[./\\\u0000-\u001f\u007f]/.test(value);
const validHash = (value: string) => /^[a-f0-9]{64}$/.test(value);
const operationKeyValid = (value: string) => /^[a-zA-Z0-9_-]{8,128}$/.test(value);

const parseValues = (form: FormValues): { operationKey: string; selection: AccountBindingSelection } | null => {
  const revision = Number(form.expectedBindingRevision);
  const targetPersonId = Number(form.targetPersonId);
  if (!safeId(form.authUid, 128) || (form.formerUserId !== '' && !safeId(form.formerUserId, 512))
    || !safeId(form.targetUserId, 512) || form.formerUserId === form.targetUserId
    || !Number.isSafeInteger(targetPersonId) || targetPersonId <= 0
    || !operationKeyValid(form.operationKey) || !validHash(form.frozenInventorySha256)
    || form.expectedBindingRevision.trim() === '' || !Number.isSafeInteger(revision)
    || revision < 0 || revision >= Number.MAX_SAFE_INTEGER
    || (revision === 0 ? form.expectedBindingOperationDigest !== '' : !validHash(form.expectedBindingOperationDigest))) return null;
  return {
    operationKey: form.operationKey,
    selection: {
      authUid: form.authUid,
      formerUserId: form.formerUserId || null,
      targetUserId: form.targetUserId,
      targetPersonId,
      frozenInventorySha256: form.frozenInventorySha256,
      expectedBindingRevision: revision,
      ...(revision > 0 ? { expectedBindingOperationDigest: form.expectedBindingOperationDigest } : {}),
    },
  };
};

const AccountBindingPrepareDialog: React.FC<Props> = ({ open, transport, onClose, onPrepared }) => {
  const [form, setForm] = useState<FormValues>(emptyValues);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const [attemptLocked, setAttemptLocked] = useState(false);
  const attempt = useRef<{ controller: AccountBindingReviewController } | null>(null);
  const generation = useRef(0);

  useEffect(() => {
    generation.current += 1;
    attempt.current = null;
    setForm(emptyValues);
    setBusy(false);
    setError(false);
    setAttemptLocked(false);
    return () => { generation.current += 1; attempt.current = null; };
  }, [open, transport]);

  const close = () => {
    if (busy) return;
    generation.current += 1;
    attempt.current = null;
    onClose();
  };
  const update = (field: keyof FormValues) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setForm(current => ({ ...current, [field]: event.target.value }));
  };
  const prepare = async () => {
    let currentAttempt = attempt.current;
    if (!currentAttempt) {
      const parsed = parseValues(form);
      if (!parsed) return;
      try {
        const controller = createAccountBindingReviewController(transport, parsed.operationKey, parsed.selection);
        currentAttempt = { controller };
        attempt.current = currentAttempt;
      } catch {
        setError(true);
        return;
      }
      setAttemptLocked(true);
    }
    const requestGeneration = generation.current;
    const isCurrent = () => generation.current === requestGeneration;
    setBusy(true);
    setError(false);
    try {
      await currentAttempt.controller.prepare();
      if (!isCurrent()) return;
      await currentAttempt.controller.preview();
      if (!isCurrent()) return;
      onPrepared(currentAttempt.controller);
    } catch {
      if (isCurrent()) setError(true);
    } finally {
      if (isCurrent()) setBusy(false);
    }
  };

  const valid = !!parseValues(form);
  const fields: Array<[keyof FormValues, string, string?]> = [
    ['authUid', 'Auth UID'], ['formerUserId', 'Former users document ID (optional)'],
    ['targetUserId', 'Target users document ID'], ['targetPersonId', 'Target person ID'],
    ['operationKey', 'Review operation key'], ['frozenInventorySha256', 'Frozen inventory SHA-256'],
    ['expectedBindingRevision', 'Expected binding revision'],
    ['expectedBindingOperationDigest', 'Expected binding operation digest'],
  ];
  return (
    <Dialog open={open} onClose={close} disableEscapeKeyDown={busy} maxWidth="sm" fullWidth aria-labelledby="account-binding-prepare-title">
      <DialogTitle id="account-binding-prepare-title">Prepare account binding review</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Alert severity="info">
            This review changes account access while preserving person records. The reviewed maintenance freeze, external reference checks, and Auth admin drain must be completed separately; this form does not perform them.
          </Alert>
          {error && <Alert severity="error">Could not prepare a current account binding plan. Retry with the same values or close this review.</Alert>}
          {fields.map(([field, label]) => (
            <TextField key={field} label={label} value={form[field]} onChange={update(field)}
              disabled={busy || attemptLocked} inputProps={{ maxLength: field === 'authUid' ? 128 : 512 }} />
          ))}
          <Typography variant="body2">Expected revision is 0 for an unbound account. For later revisions, provide the matching operation digest.</Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={busy}>Close</Button>
        <Button variant="contained" onClick={prepare} disabled={busy || !valid}>
          {busy ? <CircularProgress size={20} aria-label="Preparing review" /> : attemptLocked ? 'Retry prepare' : 'Prepare and preview'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AccountBindingPrepareDialog;
